




import { Vue, Component } from 'vue-property-decorator';

import SignUpService from '@/api/SingupService';
import BusinessService from '@/api/BusinessService';
import StartGuideService from '@/api/StartGuideService';
import MerchantService from '@/api/MerchantService';
import BillingService from '@/api/BillingService';
import { setAppCookie } from '@/api/index';

import {
  BusinessOnboardingState,
  BusinessOnboardingStoreDetailsForm,
  BusinessOnboardingYourStoreForm,
  EBusinessOnboardingInitialFlow
} from '@/models/business-onboarding';
import { IBusinessCreateData, IBusinessType } from '@/models/business';

import { omitNilProperties } from '@/utils/omitNilProperties';
import eventBus from '@/utils/eventBus';
import { createBusinessPayload } from '@/utils/createBusinessPayload';
import flagKeys from '@/constants/flagKeys';
import {
  BUSINESS_ONBOARDING_UUID_CREATION,
  mapOauthStateToStoreDetailsForm
} from '@/store/modules/businessOnboarding';

import {
  getOauthState,
  isExternalOauthSessionOnline,
  isOauthSession,
  removeOauthState,
  TRACKING_START_SIGNUP_SESSION_KEY
} from '@/router/router-utils';

import { clearSessionSignup } from '@/views/Signup/session';
import { sessionKey } from '@/views/Signup/SignupModel';

import { DashboardScreen } from '@/api/enums/dashboard-screen.enum';
import { updateRegistration } from '@/utils/updateRegistration';
import { IProvider } from '@/models/provider';
import {
  showModalBillingLocationBenefits,
  showModalOnboardingCall
} from '@/utils/modal';
import segmentIo from '@/utils/segment.io';
import diff from '@/utils/getDifferenceObjects';
import { OnboardingURL } from '../Onboarding/helpers';
import { SmartBusinessSignupData } from '@/models/smart-business-signup';
import uuidv4 from '@/utils/uuidv4';
import recaptchaRoutesKeys from '@/constants/recaptchaRoutesKeys';
import { EAutomaticCampaignName } from '@/models/sell-coins';
import CoinSetupService from '@/api/CoinSetupService';
import ImageUploadService from '@/api/ImageUploadService';
import OffersService from '@/api/OffersService';
import SellCoinsService from '@/api/SellCoinsService';

@Component({
  components: {}
})
export default class BusinessOnboardingModel extends Vue {
  signUpService = new SignUpService();
  startGuideService = new StartGuideService();
  merchantService = new MerchantService();
  businessService = new BusinessService();
  billingService = new BillingService();
  coinSetupService = new CoinSetupService();
  imageUploadService = new ImageUploadService();
  offerService = new OffersService();
  sellCoinsService = new SellCoinsService();

  IBusinessType = IBusinessType;
  loading = false;

  hasFinishTracking = true;

  get yourStoreFormData() {
    return this.$store.getters[
      'businessOnboarding/getYourStoreForm'
    ] as BusinessOnboardingYourStoreForm;
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get isNewDirectLinkSignup() {
    return !isExternalOauthSessionOnline();
  }

  get businessOnboardingState(): BusinessOnboardingState {
    return this.$store.getters[
      'businessOnboarding/getBusinessOnboardingState'
    ] as BusinessOnboardingState;
  }

  private get onboardingCategories() {
    return this.$store.getters['businessOnboarding/getCategories'];
  }

  private get onboardingSelectedCategory() {
    return this.$store.getters['businessOnboarding/getSelectedCategory'];
  }

  private get onboardingStoreDetailFormData() {
    return this.$store.getters[
      'businessOnboarding/getStoreDetailsForm'
    ] as BusinessOnboardingStoreDetailsForm;
  }

  get uuid() {
    return localStorage.getItem(BUSINESS_ONBOARDING_UUID_CREATION) || '';
  }

  private get businessName() {
    if (!this.$store.getters['business/getBusinessInfo'].business) return '';
    return this.$store.getters['business/getBusinessInfo'].business.name;
  }

  private get businessId() {
    if (!this.$store.getters['business/getBusinessInfo'].business) return '';
    return this.$store.getters['business/getBusinessInfo'].business.id;
  }

  private get businessSubscriptionStatus() {
    if (!this.$store.getters['business/getBusinessInfo'].business) return '';
    return this.$store.getters['business/getBusinessInfo'].business
      .subscriptionStatus;
  }

  get hasAICoinNetworkFeatures() {
    if (!this.smartBusinessSignupData) return false;

    return (
      this.smartBusinessSignupData.giftPercentageRecommendation.enabled ||
      this.smartBusinessSignupData.loyaltyPercentageRecommendation.enabled
    );
  }

  get smartBusinessSignupData(): SmartBusinessSignupData {
    return (
      this.$store.getters['businessOnboarding/getSmartBusinessSignup'] || {}
    );
  }

  get enabledAIOffers() {
    if (
      !this.smartBusinessSignupData.offersRecommendation ||
      !this.smartBusinessSignupData.offersRecommendation.enabled
    )
      return [];

    return this.smartBusinessSignupData.offersRecommendation.offers.filter(
      offer => offer.enabled
    );
  }

  get businessUUID() {
    return localStorage.getItem(BUSINESS_ONBOARDING_UUID_CREATION) || uuidv4();
  }

  get subscriptionBilling() {
    return this.$store.getters['business/getBillingSubscription'];
  }

  private generateBusinessPayload(): Partial<IBusinessCreateData> {
    const category = this.onboardingCategories.find(
      (categ: any) => categ.value === this.onboardingSelectedCategory.id
    );
    const otherValue = this.onboardingSelectedCategory.otherValue;
    const data = createBusinessPayload(this.businessOnboardingState);
    const signupFrom = localStorage.getItem(TRACKING_START_SIGNUP_SESSION_KEY);

    return {
      ...data,
      categoryId: category.value,
      otherCategory: otherValue,
      onlineMerchantSignup: isExternalOauthSessionOnline(),
      signupFrom: signupFrom
        ? signupFrom
        : isExternalOauthSessionOnline()
        ? EBusinessOnboardingInitialFlow.PROVIDER
        : EBusinessOnboardingInitialFlow.DIRECT_LINK,
      coinNetworkSignup:
        (this.flags.includes(flagKeys.billingModuleV2) &&
          data.type === IBusinessType.INSTORE) ||
        (this.flags.includes(flagKeys.billingModuleV4) &&
          data.type === IBusinessType.BOTH)
    };
  }

  private async handleCreateBusinessProspect() {
    const businessData = this.generateBusinessPayload();

    const payload = {
      ...omitNilProperties(businessData),
      businessName: businessData.name,
      businessId: this.businessOnboardingState.prefilledBusinessId
    };

    try {
      this.loading = true;

      await this.signUpService.businessProspect(omitNilProperties(payload));
    } finally {
      this.loading = false;
    }
  }

  async handleUpdateBusiness() {
    const { status, data } = await this.businessService.getBusinessInfo();

    if (status !== 200) return;

    eventBus.$emit('setBusinessInfo', data);

    this.$store.dispatch('business/SET_BUSINESS_INFO', {
      ...data,
      manager: data.loggedInManager
    });
  }

  private async handleUpdateProviders() {
    const response = await this.merchantService.listProviders();
    const providers = response.data.providers as IProvider[];
    await this.$store.dispatch('merchant/SET_PROVIDERS_LIST', providers);
  }

  async saveBusinessLead() {
    try {
      const { data } = await this.onSaveBusinessLead();
      if (data)
        this.$store.dispatch(
          'businessOnboarding/SET_BUSINESS_LEAD_ID',
          data.id
        );
    } catch (error) {
      console.error(error);
    }
  }

  async onSaveBusinessOnlineLead() {
    const oauthState = getOauthState();

    const {
      businessName,
      website,
      phone,
      address
    }: any = mapOauthStateToStoreDetailsForm(oauthState);

    return this.signUpService.businessLead({
      ...this.generateBusinessPayload(),
      ownerMail: oauthState.businessData.ownerMail,
      provider: oauthState.provider.name,
      originalNameFromProvider: businessName,
      originalWebsiteFromProvider: website,
      originalPhoneFromProvider: phone && phone.number,
      originalCountryFromProvider: address && address.countryName
    });
  }

  async onSaveBusinessLead() {
    const { subCategories } = this.onboardingStoreDetailFormData;
    const payload = omitNilProperties(this.generateBusinessPayload());
    return this.signUpService.businessLead({
      ...payload,
      subcategories: subCategories,
      provider: 'fidel'
    });
  }

  onEditedImportedBusinessDate(oauthState: any) {
    const {
      businessName,
      phone,
      website,
      address
    }: any = mapOauthStateToStoreDetailsForm(oauthState);

    const payload = createBusinessPayload(this.businessOnboardingState);

    const data = {
      ...payload,
      phone: payload.phone ? payload.phone.replace(/[^0-9]+/g, '') : '',
      email: this.businessOnboardingState.account.form.email
    };

    const imported: any = {
      name: businessName.slice(0, 70),
      website,
      email: oauthState.businessData.ownerMail
    };

    if (address) imported.country = address.countryName;
    if (phone.number) imported.phone = phone.number.replace(/[^0-9]+/g, '');

    const edited = Object.keys(diff(imported, data));
    const format: any = {
      name: 'Business Name',
      website: 'Website',
      email: 'Email',
      phone: 'Phone',
      country: 'Country'
    };

    if (edited.length) {
      const props: any = {};
      Object.keys(format).forEach(key => {
        props[`${format[key]} edited`] = !!edited.includes(key);
      });

      segmentIo.track('NPO Sign Up | Imported Data Edit', props);
    }
  }

  async onCreateBusiness() {
    if (this.businessOnboardingState.prefilledBusinessId) {
      this.handleCreateBusinessProspect();

      return {
        flow: 'pre-filled-business'
      };
    }

    try {
      this.loading = true;

      const payload = omitNilProperties(this.generateBusinessPayload());

      const createBusiness = () => {
        const transformedPayload = {
          ...payload,
          ...(this.uuid && { frontUid: this.uuid })
        };

        if (isOauthSession()) {
          const token = getOauthState().query.jwt;
          return this.signUpService.createBusinessWithProvider(
            transformedPayload,
            token
          );
        } else {
          return this.signUpService.createBusiness(transformedPayload);
        }
      };

      const { data } = await createBusiness();

      const { access_token } = data;
      // For OAuth/provider signups (e.g. Wix), the business creation endpoint
      // returns a new token in the response body but does not issue a new cookie.
      // Exchange it so subsequent requests use the manager+business session.
      if (isOauthSession() && access_token) {
        await this.signUpService.exchangeUrlTokenForCookie(access_token);
      } else {
        this.signUpService.setCredentials(access_token);
      }

      let isFirstBusiness = !this.$store.getters['business/getBusinessInfo']
        .business;
      if (isOauthSession()) {
        isFirstBusiness = !Object.keys(getOauthState()).includes(
          'fromSelectBusinessRoute'
        );
      }

      sessionStorage.removeItem(sessionKey);
      clearSessionSignup();
      await Promise.all([
        this.handleUpdateProviders(),
        this.handleUpdateBusiness()
      ]);
      if (this.hasFinishTracking)
        segmentIo.track(
          `${
            this.isNewDirectLinkSignup ? 'BFR' : 'NPO'
          } Sign Up | Completed Merchant Signup`,
          {
            'Is First Business': isFirstBusiness,
            'Signup from Online Provider': isExternalOauthSessionOnline(),
            'Direct Link': !isExternalOauthSessionOnline()
          }
        );
      removeOauthState();
    } finally {
      this.loading = false;
    }
  }

  finishOnboarding(
    isPreFilledBusinessFlow = false,
    showAnimationLoadingScreen = false
  ) {
    if (isPreFilledBusinessFlow) {
      this.$router.push('/signup-waiting-approve');

      return;
    }

    const flags = this.$store.getters['flags/getFlags'];
    const business = this.$store.getters['business/getBusinessInfo'].business;

    if (
      business &&
      business.type === IBusinessType.INSTORE &&
      flags.includes(flagKeys.newMerchantPortal)
    ) {
      setAppCookie('instore');
      window.location.href = `/?${+Date.now()}`;

      return;
    }

    this.billingService
      .getBillingOverviewSubscription()
      .then(({ data: subscription }) => {
        this.$store.dispatch('business/SET_BILLING_SUBSCRIPTION', subscription);
      });

    this.businessService.getRewardStatusList().then(({ data: rewardData }) => {
      this.$store.dispatch('business/SET_REWARD_STATUS', rewardData);
    });

    this.$store.dispatch('startGuide/RESET_START_GUIDE');
    this.$store.dispatch('startGuide/GET_TASKS');

    this.$router.push({
      name: 'home',
      params: {
        resetBusinessOnboarding: 'true',
        showStartGuideHighlight: !isExternalOauthSessionOnline() ? 'true' : '',
        showBillingLocationBenefitsModal: showModalBillingLocationBenefits()
          ? 'true'
          : '',
        showAnimationLoadingScreen: showAnimationLoadingScreen ? 'true' : ''
      },
      query: {
        ...(showModalOnboardingCall()
          ? { modal: 'scheduleOnboardingModal' }
          : {})
      }
    });
  }

  shouldSentRecaptcha(featureFlag: string) {
    return this.flags.includes(featureFlag);
  }

  getImagePath(url: string) {
    return url.slice(url.indexOf('images/'), url.indexOf('.png') + 4);
  }

  getIsAIImage(url: string) {
    return url.includes('ai-core');
  }

  async getRecaptcha(route: string, featureFlag: string) {
    let recaptcha = '';

    if (this.shouldSentRecaptcha(featureFlag)) {
      recaptcha = await this.$recaptcha(route);
    }

    return recaptcha;
  }

  async setOffersImagesIds() {
    const offers = this.enabledAIOffers;

    let offerImages: {
      data: { id?: string; title?: string };
    }[] = await Promise.all(
      offers.map(offer =>
        this.getImagePath(offer.image.url) && !offer.image.id
          ? this.imageUploadService.getImageIdByPath(
              this.getImagePath(offer.image.url)
            )
          : { data: { id: offer.image.id } }
      )
    );
    offerImages = offerImages.map((response, index) => ({
      data: {
        id: response.data.id,
        title: offers[index].title
      }
    }));

    this.$store.dispatch('businessOnboarding/SET_SMART_BUSINESS_SIGNUP', {
      ...this.smartBusinessSignupData,
      offersRecommendation: {
        ...this.smartBusinessSignupData.offersRecommendation,
        offers: this.smartBusinessSignupData.offersRecommendation.offers.map(
          offer => {
            const image = offerImages.find(
              offerData => offer.title === offerData.data.title
            );

            if (offers.length > 0 && image)
              return {
                ...offer,
                image: {
                  ...offer.image,
                  id: image.data.id
                }
              };
            return offer;
          }
        )
      }
    });
  }

  setupAIGift() {
    if (!this.smartBusinessSignupData.giftPercentageRecommendation.enabled)
      return [];
    return [
      this.businessService.updateSellCoins(true, DashboardScreen.AIFlow),
      this.sellCoinsService.createSellingCoinsCampaign({
        name: EAutomaticCampaignName.loyalt,
        incentiveType: 'bonus',
        incentiveValue: this.smartBusinessSignupData
          .giftPercentageRecommendation.percentage
      })
    ];
  }

  setupAILoyalty() {
    return this.smartBusinessSignupData.loyaltyPercentageRecommendation.enabled
      ? [
          this.signUpService.setupRules({
            refundsLimitDays: 0,
            coinPurchaseRatio:
              100 /
              this.smartBusinessSignupData.loyaltyPercentageRecommendation
                .percentage,
            coinRewardPercentage: this.smartBusinessSignupData
              .loyaltyPercentageRecommendation.percentage
          })
        ]
      : [];
  }

  setupAIOffers() {
    return this.enabledAIOffers.map(offer =>
      this.offerService.newOffer({
        imageSrc: offer.image.url,
        imageId: offer.image.id,
        discount: offer.discount,
        method: offer.method,
        rules: offer.rules,
        title: offer.title,
        type: offer.type,
        description: offer.description,
        business: this.businessId,
        specialType: offer.specialType,
        expirationDate: offer.expirationDate,
        ...(offer.avgDiscountValue
          ? { avgDiscountValue: offer.avgDiscountValue }
          : {})
      })
    );
  }

  setupAIBrandedCoin(recaptchaCoinSetupReserve: string) {
    if (
      !this.smartBusinessSignupData.generateBrandedCoins.brandedCoinSelected
        .additionalData
    )
      return [];

    const brandedCoinPayload = {
      ...this.smartBusinessSignupData.generateBrandedCoins.brandedCoinSelected
        .additionalData,
      businessId: this.businessId
    };

    return [
      this.coinSetupService.reserveCoinInformations(
        brandedCoinPayload,
        recaptchaCoinSetupReserve
      )
    ];
  }

  async onCreateAICampaigns() {
    const recaptchaCoinSetupReserve = await this.getRecaptcha(
      recaptchaRoutesKeys.businessCoinSetupReserve,
      flagKeys.businessCoinSetupReserveCaptcha
    );

    await this.setOffersImagesIds();

    await Promise.all([
      ...this.setupAIBrandedCoin(recaptchaCoinSetupReserve),
      ...this.setupAIOffers(),
      ...this.setupAILoyalty(),
      ...this.setupAIGift()
    ]);

    if (this.smartBusinessSignupData.loyaltyPercentageRecommendation.enabled) {
      await Promise.all([
        updateRegistration('manage-rewards'),
        updateRegistration('return-policy')
      ]);
    }
  }

  async redirectAIFlow(redirectType: 'push' | 'replace' = 'push') {
    if (this.hasAICoinNetworkFeatures) {
      this.$router[redirectType](OnboardingURL.AI_COIN_NETWORK);
    } else {
      await this.handleUpdateBusiness();
      this.$router[redirectType](OnboardingURL.WELCOME_TWISM);
    }
  }

  async updateBusinessCategory(categoryId: string) {
    const payload = {
      name: this.businessName,
      categoryId: categoryId
    };

    return await this.businessService.updateProfile(payload);
  }
}
